import { keycloak } from '@/main'

/**
 * These are the keys for the permissions that can be checked in the
 * EssentialsPermissionsControl. They are defined as constants to ensure
 * consistency across the application when checking permissions. Each key
 * corresponds to a specific permission that a user may have based on their
 * subscription status and roles in Keycloak. The values of these keys are used in
 * the buildEssentialsPermissions method to construct the permissions object, and
 * they can be accessed through the get method to check if a user has a specific
 * permission.
 */
export const PermissionKeys = {
  // Policy & Billing
  isPolicyHolder: 'isPolicyHolder',
  canManageBilling: 'canManageBilling',
  // User Management
  canInviteUsers: 'canInviteUsers',
  canViewUsers: 'canViewUsers',
  canEditUsers: 'canEditUsers',
  canEditOwnUser: 'canEditOwnUser',
  canDeleteUsers: 'canDeleteUsers',
  // Vehicle Management
  canCreateVehicles: 'canCreateVehicles',
  canViewVehicles: 'canViewVehicles',
  canEditVehicles: 'canEditVehicles',
  canDeleteVehicles: 'canDeleteVehicles',
  canBookVehicles: 'canBookVehicles',
  // Location Management
  canCreateLocations: 'canCreateLocations',
  canViewLocations: 'canViewLocations',
  canEditLocations: 'canEditLocations',
  canDeleteLocations: 'canDeleteLocations',
  // Trip Management
  canCreateTrips: 'canCreateTrips',
  canViewTrips: 'canViewTrips',
  canEditTrips: 'canEditTrips',
  canDeleteTrips: 'canDeleteTrips',
  canAssignTrips: 'canAssignTrips',
  canUnassignTrips: 'canUnassignTrips',
  canBePassengersOnTrips: 'canBePassengersOnTrips',
  canEditTripPresets: 'canEditTripPresets',
  // Charger Management
  canCreateChargers: 'canCreateChargers',
  canViewChargers: 'canViewChargers',
  canEditChargers: 'canEditChargers',
  canDeleteChargers: 'canDeleteChargers',
  canBookChargers: 'canBookChargers',
} as const

export type PermissionKey = (typeof PermissionKeys)[keyof typeof PermissionKeys]

type PermissionSet = Record<PermissionKey, boolean>

export class EssentialsPermissionsControl {
  private permissions: PermissionSet

  private constructor(isSubscribed: boolean) {
    this.permissions = Object.freeze(this.buildEssentialsPermissions(isSubscribed))
  }

  /**
   * Factory method to create an instance of EssentialsPermissionsControl. This
   * method checks the user's Keycloak roles to determine if they are subscribed
   * to the essentials API and initializes the instance accordingly.
   *
   * @returns an instance of EssentialsPermissionsControl with the user's subscription status set.
   */
  static create() {
    const isSubscribed = keycloak.hasResourceRole('subscribed', 'essentials-api')
    return new EssentialsPermissionsControl(isSubscribed)
  }

  private buildEssentialsPermissions(isSubscribed: boolean): PermissionSet {
    // Implementation for building permission set based on subscription status
    return {
      isPolicyHolder: isSubscribed,
      canManageBilling: isSubscribed,
      canInviteUsers: isSubscribed,
      canViewUsers: true,
      canEditUsers: isSubscribed,
      canEditOwnUser: true,
      canDeleteUsers: isSubscribed,
      canCreateVehicles: true,
      canViewVehicles: true,
      canEditVehicles: true,
      canDeleteVehicles: true,
      canBookVehicles: true,
      canCreateLocations: true,
      canViewLocations: true,
      canEditLocations: true,
      canDeleteLocations: true,
      canCreateTrips: true,
      canViewTrips: true,
      canEditTrips: true,
      canDeleteTrips: true,
      canAssignTrips: true,
      canUnassignTrips: true,
      canBePassengersOnTrips: true,
      canCreateChargers: false,
      canViewChargers: false,
      canEditChargers: false,
      canDeleteChargers: false,
      canBookChargers: false,
      canEditTripPresets: isSubscribed,
    }
  }

  /**
   * Refreshes the user's permissions by checking their Keycloak roles again. This
   * should be called after any action that could change the user's subscription
   * status, such as accepting an invite or creating a fleet.
   *
   * @param forceTokenRefresh optional boolean parameter that, if true, forces a
   * refresh of the Keycloak token before checking the user's roles. This can be
   * useful if the user's subscription status may have changed recently and we want
   * to ensure we have the most up-to-date information. If false or not provided,
   * the function will check the user's roles without refreshing the token, which
   * may be faster but could potentially return outdated information if the user's
   * subscription status has changed since the last token refresh.
   */
  public async refresh(forceTokenRefresh = false) {
    if (forceTokenRefresh) {
      await keycloak.updateToken(-1)
    }
    const isSubscribed = keycloak.hasResourceRole('subscribed', 'essentials-api')
    this.permissions = Object.freeze(this.buildEssentialsPermissions(isSubscribed))
  }

  /**
   * Generic permission getter for any permission key. This allows for dynamic access
   *
   * @param permission the permission key to check
   * @returns {boolean} True if the user has the permission, false otherwise.
   */
  public get(permission: PermissionKey): boolean {
    return this.permissions[permission]
  }
}
